import { Location } from '@angular/common';
import { DestroyRef, inject, InjectionToken, signal, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, UrlSegment } from '@angular/router';
import { RouteService } from '@app/core/route.service';
import { AppRouteTranslationMap } from '@app/shared/app.route.enum';
import { __ } from '@app/shared/functions/object.functions';
import { of } from 'rxjs';
import { filter, mergeMap } from 'rxjs/operators';

import { Logger } from '../logger/logger.service';

const log = new Logger('BreadcrumbComponent');

export interface WithBreadcrumbs {
  setBreadCrumb(): void;
}

export class BreadcrumbSegment {
  title!: string;
  routerLink!: string;
  path!: string;
  viewName!: string;
}

export const APP_ROUTE = new InjectionToken<Record<string, string>>('AppRoute');
export const APP_TITLE = new InjectionToken<string>('AppTitle');

/**
 * The breadcrumb service creates breadcrumbs by subscribing to the urlSegments of the active route and
 * to breadcrumbSegments of the breadcrumb service. The routerLinks of the breadcrumbs will be build and
 * displayed with a translateable title.
 *
 * WARNING: - DOES NOT WORK ON SAME ROUTE NAVIGATION (COMPONENT WILL NOT BE INITIALIZED AGAIN WHEN E.G.
 *            ONLY THE ID PARAMETER CHANGES IN A ROUTE).
 *          - MAY NOT WORK WITH MORE A GREATER HIEARCHY THAN E.G. limited/editions/3/edit due to
 *            this.breadcrumbs.slice
 */
@Service({ autoProvided: false })
export class BreadcrumbsService {
  readonly breadcrumbSegments = signal<BreadcrumbSegment[]>([]);

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly location = inject(Location);
  private readonly appRoute = inject(APP_ROUTE);
  private readonly appTitleKey = inject(APP_TITLE);
  private readonly destroyRef = inject(DestroyRef);
  private currentUrl: string = '';

  constructor() {
    if (__.IsNullOrUndefined(this.appRoute)) {
      throw new Error(
        'The injection token APP_ROUTE has not been provided. The breadcrumbs service cannot be used without it.',
      );
    }

    this.init();
  }

  /**
   * Adds a new breadcrumb to the list of breadcrumbs based on the provided title, optional identifier and optional view name.
   * If the identifier is left empty, no routing link will be produced for navigation calculation. The view name can be used
   * to further specify the current breadcrumb with e.g sub navigation names, which will be displayed without a router link
   * behind the provided title.
   *
   * @param title The title that will be displayed in the breadcrumb list
   * @param  identifier The optional identifier that will be used for routing purposes
   * @param viewName The optional view name to further specify sub navigation
   */
  addBreadcrumb(title: string, identifier?: string, viewName?: string): void {
    if (__.IsNullOrUndefinedOrEmpty(title)) {
      log.error('The title of a breadcrumb has not been set');
      return;
    }

    const _identifier = identifier || null;
    const _viewName = viewName || null;

    const segments = this.breadcrumbSegments();
    if (segments.findIndex((q) => q.title === title && q.path === identifier) > -1) {
      log.error('The same breadcrumb cannot be added twice');
      return;
    }

    const routerLink = __.IsNullOrUndefinedOrEmpty(identifier)
      ? null
      : `${this.buildRouterLinkFromPreviousBreadcrumbPaths()}${identifier}`;

    const segment = Object.assign(new BreadcrumbSegment(), {
      routerLink,
      title,
      path: _identifier,
      viewName: _viewName,
    } as BreadcrumbSegment);
    this.breadcrumbSegments.set([...segments, segment]);
  }

  removeLast() {
    this.breadcrumbSegments.update((segments) => segments.slice(0, -1));
  }

  /**
   * Gets the current breadcrumbs
   *
   */
  getCurrentBreadcrumbs(): BreadcrumbSegment[] {
    return this.breadcrumbSegments();
  }

  private init(): void {
    this.breadcrumbSegments.set([
      Object.assign(new BreadcrumbSegment(), {
        routerLink: '/',
        title: this.appTitleKey,
        path: '/',
      } as BreadcrumbSegment),
    ]);

    this.router.events
      .pipe(
        filter((event) => {
          return event instanceof NavigationEnd;
        }),
        mergeMap((_route) => {
          return of(RouteService.MergeUrlSegments(this.route.snapshot));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(this.calculateUrlSegments.bind(this));
  }

  private calculateUrlSegments(urlSegments: UrlSegment[]): void {
    if (this.currentUrl === this.location.path()) {
      return;
    }

    this.currentUrl = this.location.path();

    // Depending on how many url segments there are now, only remove n - 1

    const segments = this.breadcrumbSegments().slice(0, 1); // urlSegments.length
    // The first url segment should be the resource (based on REST)
    if (!__.IsNullOrUndefined(urlSegments[0])) {
      const validRouteKeys: string[] = Object.keys(this.appRoute);
      const validRoutes = validRouteKeys.map((key: string) => this.appRoute[key]!);

      if (validRoutes.find((q) => q === urlSegments[0]!.path)) {
        segments.push(
          Object.assign(new BreadcrumbSegment(), {
            routerLink: `${this.buildRouterLinkFromPreviousBreadcrumbPaths(segments)}${urlSegments[0].path}`,
            title: this.getTitleOfUrlPath(urlSegments[0].path),
            path: urlSegments[0].path,
          }) as BreadcrumbSegment,
        );
      } else {
        log.warn(`The route ${urlSegments[0]} is not a valid route of the AppRoute enum`);
      }
    }
    // The rest of the url segments have to be handled in the components using the breadcrumb.service

    this.breadcrumbSegments.set(segments);
  }

  private getTitleOfUrlPath(path: string): string {
    return (AppRouteTranslationMap.Translations as Record<string, string>)[path] ?? path;
  }

  private buildRouterLinkFromPreviousBreadcrumbPaths(segments = this.breadcrumbSegments()): string {
    const path = segments
      .map((segment) => segment.path)
      .filter((segmentPath) => !__.IsNullOrUndefinedOrEmpty(segmentPath))
      .flatMap((segmentPath) => segmentPath.split('/').filter(Boolean))
      .join('/');

    return path ? `/${path}/` : '/';
  }
}
